import CryptoJS from 'crypto-js';
import Cookies from 'js-cookie';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { queryClient } from '../../queryClient';

const COOKIE_NAME = 'osteomed_session';
const STORAGE_KEY = 'osteomed_user_data';
const SECRET = process.env.REACT_APP_COOKIE_SECRET || 'osteomed_dev_key';
const COOKIE_OPTIONS = {
  expires: 7,
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'strict',
};

const encryptSession = (data) =>
  CryptoJS.AES.encrypt(JSON.stringify(data), SECRET).toString();

const decryptSession = (cipher) => {
  const bytes = CryptoJS.AES.decrypt(cipher, SECRET);
  return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
};

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isRestoring, setIsRestoring] = useState(true);
  const navigate = useNavigate();

  const setUserData = (data) => {
    setUser(data);
    // Cookie: apenas token — mantém tamanho abaixo do limite de 4KB dos browsers
    Cookies.set(COOKIE_NAME, encryptSession({ token: data.token }), COOKIE_OPTIONS);
    // localStorage: sessão completa (token + user + permissions)
    localStorage.setItem(STORAGE_KEY, encryptSession(data));
  };

  useEffect(() => {
    // Tenta localStorage primeiro — contém permissions completo
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setUser(decryptSession(stored));
        setIsRestoring(false);
        return;
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
    // Fallback: cookie (sessões gravadas antes desta migração)
    const cipher = Cookies.get(COOKIE_NAME);
    if (cipher) {
      try {
        setUser(decryptSession(cipher));
      } catch {
        Cookies.remove(COOKIE_NAME);
      }
    }
    setIsRestoring(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Limpa toda sessão local (cookie + localStorage + sessionStorage + cache do React Query).
  // Usado tanto no logout manual quanto no 401 (abaixo) — deixar token/permissions velhos no
  // storage depois de uma sessão invalidada causa erro ao tentar logar de novo.
  const clearSession = () => {
    setUser(null);
    Cookies.remove(COOKIE_NAME);
    localStorage.clear();
    sessionStorage.clear();
    queryClient.clear();
  };

  useEffect(() => {
    const handleUnauthorized = () => {
      clearSession();
      navigate('/erro/401');
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleServerError = () => navigate('/erro/500');
    window.addEventListener('app:server-error', handleServerError);
    return () => window.removeEventListener('app:server-error', handleServerError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    clearSession();
    navigate('/login');
  };

  return (
    <UserContext.Provider value={{ user, isRestoring, setUser: setUserData, logout }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
